<template>
  <AppTooltip>
    <template #activator="{props}">
      <span v-bind="props" :data-locator="computedIcon.locatorValue">
        <app-material-icon
          :name="computedIcon.name"
          :class="computedIcon.class"
          size="small"
          filled
        />
      </span>
    </template>
    <template v-if="noBlockingReasons && noWarningReasons">
      {{ t('tooltipTitle.ready') }}
    </template>
    <template v-else>
      <div v-if="blockingSendingMessages.length > 0" data-locator="blocking-sending-messages">
        <span>{{ t('tooltipTitle.blockingSending') }}</span>
        <ul class="infos-list">
          <i18n-t
            v-for="(blockingSendingMessage, index) in blockingSendingMessages"
            :key="`blocking_sending_${index}`"
            :keypath="blockingSendingMessage.translationKey"
            tag="li"
            class="d-flex align-center"
            scope="global"
          >
            <template
              v-for="(param, index) in blockingSendingMessage.params"
              :key="index"
              #[index]
            >
              <span>{{ param }}</span>
            </template>
            <template #maxSignablePages>
              {{ maxSignablePages }}
            </template>
            <template #menuIcon>
              <app-material-icon
                name="more_horiz"
              />
            </template>
          </i18n-t>
        </ul>
      </div>
      <div v-else-if="blockingSigningMessages.length > 0" data-locator="blocking-signing-messages">
        <span>{{ t('tooltipTitle.blockingSigning') }}</span>
        <ul class="infos-list">
          <i18n-t
            v-for="(blockingSigningMessage, index) in blockingSigningMessages"
            :key="`blocking_signing_${index}`"
            :keypath="blockingSigningMessage.translationKey"
            tag="li"
            class="d-flex align-center"
            scope="global"
          >
            <template
              v-for="(param, index) in blockingSigningMessage.params"
              :key="index"
              #[index]
            >
              <span>{{ param }}</span>
            </template>
            <template #maxSignablePages>
              {{ maxSignablePages }}
            </template>
            <template #menuIcon>
              <app-material-icon
                name="more_horiz"
              />
            </template>
          </i18n-t>
        </ul>
      </div>

      <div v-if="warningSendingMessages.length > 0" data-locator="warning-sending-messages">
        <span>{{ t('tooltipTitle.warned') }}</span>
        <ul class="infos-list">
          <li v-for="(warningMessage, index) in warningSendingMessages" :key="`warning_to_send_${index}`">
            {{ $t(warningMessage.translationKey) }}
          </li>
        </ul>
      </div>
    </template>
  </AppTooltip>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import AppTooltip from '@/common/AppTooltip.vue'
import { useTranslation } from '@/common/utils/translation'

type EnveloppeInvalidReason = {
  reason:
    | 'fileNotSignable'
    | 'missingSigner'
    | 'noFile'
    | 'noOrder'
    | 'noRecipients'
    | 'noSigners'
    | 'noTagMultiple'
    | 'notEnoughUniqueSigners'
    | 'overlappingTags'
}
type SignatureInvalidReason = {
  reason:
    | 'firstName'
    | 'idCheck'
    | 'inactive'
    | 'lastName'
    | 'noTag'
  signer: {
    id: number,
    fullName: string
  },
}
type InvalidReason = EnveloppeInvalidReason | SignatureInvalidReason

function isSignatureInvalidReason(reason: InvalidReason): reason is SignatureInvalidReason {
  return (reason as SignatureInvalidReason).signer !== undefined;
}

const TRANSLATION_KEY_PREFIX = 'project.signing-checklist.list.SigningChecklistTableItemStatusTooltip'

export default defineComponent({
  name: 'SigningChecklistTableItemStatusTooltip',
  components: { AppTooltip },
  setup () {
    return useTranslation({ keyPrefix: TRANSLATION_KEY_PREFIX })
  },
  props: {
    reasonsForBlockingSending: {
      type: Array as PropType<Array<InvalidReason>>,
      default: () => [],
    },
    reasonsForBlockingSigning: {
      type: Array as PropType<Array<InvalidReason>>,
      default: () => [],
    },
    reasonForWarningSending: {
      type: Array as PropType<Array<InvalidReason>>,
      default: () => [],
    },
  },
  data () {
    return {
      maxSignablePages: import.meta.env.VUE_APP_MAX_SIGNABLE_PAGES,
    }
  },
  computed: {
    computedIcon (): {
      class: string,
      name: string,
      locatorValue: string,
    } {
      if (this.noBlockingReasons && this.noWarningReasons) {
        return {
          class: 'text-icon-success',
          name: 'check_circle',
          locatorValue: 'success-icon',
        }
      }

      return {
        class: 'text-icon-warning',
        name: 'warning',
        locatorValue: 'warning-icon',
      }
    },
    noBlockingReasons () {
      return this.reasonsForBlockingSending.length === 0 && this.reasonsForBlockingSigning.length === 0
    },
    noWarningReasons () {
      return this.reasonForWarningSending.length === 0
    },
    blockingSendingMessages () {
      return this.reasonsForBlockingSending.map(this.formatTooltipMessage)
    },
    blockingSigningMessages () {
      return this.reasonsForBlockingSigning.map(this.formatTooltipMessage)
    },
    warningSendingMessages () {
      return this.reasonForWarningSending.map(this.formatTooltipMessage)
    },
  },
  methods: {
    formatTooltipMessage (invalidReason: InvalidReason): {
      translationKey: string,
      params?: Record<PropertyKey, string>,
    } {
      if (isSignatureInvalidReason(invalidReason)) {
        return {
          translationKey: `${TRANSLATION_KEY_PREFIX}.blockingMessages.invalidSignature.${invalidReason.reason}`,
          params: {
            user: invalidReason.signer.fullName,
          },
        }
      }

      return {
        translationKey: `${TRANSLATION_KEY_PREFIX}.blockingMessages.invalidEnvelope.${invalidReason.reason}`,
      }
    },
  },
})
</script>

<style scoped lang="scss">
.infos-list {
  margin-inline: 8px;

  li {
    list-style-type: '- ';
  }
}
</style>
